import React, { useCallback, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { sessionService } from '@app/services';
import { SetLoginForm, SetUserSessionInfo, Session, TranslationsTypes } from '@app/models';
import { AvatarComponent, CompanyLogo, ErrorBlock, KaseyaOne } from '@app/components';
import { loginForms } from '@app/enums';
import { signInErrorClean } from '@app/store/actions';
import { useDispatch } from 'react-redux';
import { localStore } from '@app/helpers';
import { SESSION_HISTORY } from '@app/constants';
import Icon from '@app/components/Icon/index';
import { PasslyTooltip } from '@app/componentsV2';
import { Link } from '@material-ui/core';

interface IProps {
    setLoginForm: SetLoginForm;
    setUserSessionInfo: SetUserSessionInfo;
    errorKaseyaOneCode: TranslationsTypes;
    isKaseyaOneLoginEnabled?: boolean;
    handleKaseyaOneClick?: () => void;
    isLoading?: boolean;
}
interface HistoryProps {
    displayName: string;
    email: string;
    lastVisited: number;
    userUuid: string;
    username: string;
}

export const SignInList: React.SFC<IProps> = ({ setLoginForm, setUserSessionInfo, isKaseyaOneLoginEnabled, handleKaseyaOneClick, errorKaseyaOneCode, isLoading }) => {
    const { translation } = useTypedTranslation();
    const [sessions, setSessions] = useState(sessionService.getSessionHistory());
    const dispatch = useDispatch();
    const handleGoToSignIn = useCallback(() => {
        dispatch(signInErrorClean());
        setLoginForm(loginForms.unknown);
    }, []);
    const handleSignInClick = useCallback(
        (session: Session) => () => {
            setLoginForm(loginForms.selected);
            setUserSessionInfo({
                displayInfo: session.displayName,
                username: session.username,
                userUuid: session.userUuid
            });
        },
        []
    );
    const forgetUser = useCallback(
        (username: string) => (e: React.MouseEvent) => {
            e.stopPropagation();

            sessions.forEach((session: HistoryProps) => {
                if (session.username.toLowerCase() === username.toLowerCase()) {
                    sessions.splice(sessions.indexOf(session), 1);
                }
            });

            setSessions([...sessions]);

            localStore.removeItem(SESSION_HISTORY);
            localStore.setItem('session_history', JSON.stringify(sessions));
        },
        [sessions]
    );

    return (
        <>
            <CompanyLogo />
            <div className='loginList'>
                <div className='loginList__form-text'>{translation('SIGNIN_OPTIONS_TITLE')}</div>
                <ErrorBlock className='login__error' isError={!!errorKaseyaOneCode} errorCode={errorKaseyaOneCode} />
                <ul className='loginList__wrapper'>
                    {sessions.map((session: Session) => (
                        <li className='userSignInList' key={session.userUuid} onClick={handleSignInClick(session)}>
                            <AvatarComponent className='userSignInList__avatar' userId={session.userUuid} size='xs' imageType='blue' />
                            <div className='userSignInList__info'>
                                <div className='userSignInList__info-displayName'>{session.displayName}</div>
                                <div className='userSignInList__info-userName'>{session.username}</div>
                            </div>
                            <div className='userSignInList__info-forgot' onClick={forgetUser(session.username)}>
                                <PasslyTooltip title={translation('SIGNIN_FORGET_USER')}>
                                    <div>
                                        <Icon icon='forgetUserIco' className='userSignInList__info-forgot-icon' />
                                    </div>
                                </PasslyTooltip>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            <div className='userSignInList__footer'>
                <Link className='userSignInList__button' id='otherSigninOptionsButton' color='primary' underline='none' onClick={handleGoToSignIn}>
                    {translation('SIGNIN_OPTIONS_OTHER')}
                </Link>
            </div>
            {isKaseyaOneLoginEnabled && <KaseyaOne onClick={handleKaseyaOneClick} isLoading={isLoading} />}
        </>
    );
};
